import { useState } from 'react';
import { Flame, Zap, Trophy, Share2 } from 'lucide-react';
import { BadgeDisplay } from './BadgeDisplay';
import { getLevelInfo } from '../../utils/scoring';
import * as storage from '../../utils/storage';
import { GeometricAvatar } from '../shared/GeometricAvatar';

const isValidEmail = (value) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim());

export const ProfileView = ({
  isDataLoaded,
  username,
  bio,
  userId,
  badges,
  BADGE_DEFINITIONS,
  getUserStats,
  sendItScore,
  getRecentSends,
  setUsernameInput,
  setBioInput,
  setShowEditProfile,
  setShowUsernameSetup,
  shareToSocial,
  soundEnabled,
  setSoundEnabled,
  playSound,
  notificationPermission,
  requestNotificationPermission,
  isDev,
  testAdvanceDay,
  resetAllData,
  isPremium,
  openUpgradeModal,
  isAnonymousUser,
  hasCloudSession,
  authEmail,
  authEmailConfirmed,
  premiumAccountPrompt,
  linkAnonymousAccount,
}) => {
  const [accountEmail, setAccountEmail] = useState('');
  const [accountPassword, setAccountPassword] = useState('');
  const [accountPasswordConfirm, setAccountPasswordConfirm] = useState('');
  const [accountLinking, setAccountLinking] = useState(false);
  const [accountLinkError, setAccountLinkError] = useState('');
  const [accountLinkMessage, setAccountLinkMessage] = useState('');

  const handleLinkAccount = async (event) => {
    event.preventDefault();
    setAccountLinkError('');
    setAccountLinkMessage('');

    const email = accountEmail.trim().toLowerCase();
    if (!isValidEmail(email)) {
      setAccountLinkError('Enter a valid email address.');
      return;
    }
    if (accountPassword.length < 8) {
      setAccountLinkError('Use a password with at least 8 characters.');
      return;
    }
    if (accountPassword !== accountPasswordConfirm) {
      setAccountLinkError('Passwords do not match.');
      return;
    }

    setAccountLinking(true);
    try {
      await linkAnonymousAccount(email, accountPassword);
      setAccountLinkMessage(
        'Check your inbox and confirm this email before buying SendIt Pass. Use this exact address at checkout; your progress remains on this account.'
      );
      setAccountPassword('');
      setAccountPasswordConfirm('');
    } catch (error) {
      console.error('Failed to link login to anonymous account:', error);
      setAccountLinkError(error.message || 'Could not link this login. Please try again.');
    } finally {
      setAccountLinking(false);
    }
  };

  return (
    <div className="space-y-4 sm:space-y-5 px-3 sm:px-4 animate-fade-in">
      {!isDataLoaded ? (
        <div className="bg-gray-900 border-3 border-blue-500 p-12 text-center">
          <div className="animate-pulse">
            <div className="text-4xl mb-4">⏳</div>
            <p className="text-blue-400 font-black text-xl uppercase">LOADING...</p>
          </div>
        </div>
      ) : (
        <>
          <div className="bg-gradient-to-r from-blue-600 to-indigo-600 border-3 sm:border-4 border-blue-400 p-4 sm:p-5 skew-x-[-2deg]">
            <h2 className="text-3xl sm:text-4xl font-black text-white uppercase tracking-tight skew-x-[2deg]">YOUR PROFILE</h2>
          </div>

          {/* Profile Header */}
          <div className="bg-gradient-to-br from-gray-900 to-black border-4 border-blue-500 p-6 sm:p-8">
            <div className="flex items-center gap-4 sm:gap-6 mb-4">
              <GeometricAvatar username={username || 'Anonymous'} size={80} />
              <div className="flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <h3 className="text-xl sm:text-3xl font-black text-blue-400 uppercase truncate">{username || 'Anonymous'}</h3>
                  {isPremium && (
                    <span className="inline-flex items-center gap-1 bg-yellow-900 border border-yellow-500 px-2 py-0.5 text-yellow-400 font-black text-xs uppercase tracking-wide flex-shrink-0">
                      ⚡ SENDIT PASS ✓
                    </span>
                  )}
                </div>
                {bio && (
                  <p className="text-gray-300 text-sm font-bold italic mt-1">"{bio}"</p>
                )}
                {(() => {
                  const memberTimestamp = userId?.includes('_')
                    ? parseInt(userId.split('_')[1])  // old format
                    : null;                             // new UUID format — will show fallback
                  const memberSince = memberTimestamp ? new Date(memberTimestamp).toLocaleDateString() : 'Day 1';
                  return (
                    <p className="text-gray-500 text-sm font-bold mt-1">
                      Member since {memberSince}
                    </p>
                  );
                })()}
              </div>
            </div>

            {/* Badges Display */}
            {badges.length > 0 && (
              <div className="mb-4 pb-4 border-b-2 border-gray-800">
                <h4 className="text-sm font-black text-gray-400 uppercase mb-3">BADGES ({badges.length}/{Object.keys(BADGE_DEFINITIONS).length})</h4>
                <div className="flex flex-wrap gap-2">
                  {badges.slice(-5).reverse().map(badgeId => {
                    const badge = BADGE_DEFINITIONS[badgeId];
                    if (!badge) return null;
                    return (
                      <div
                        key={badgeId}
                        className={`${badge.bgColor} ${badge.borderColor} border-3 px-3 py-2 flex items-center gap-2 group hover:scale-105 transition-transform cursor-pointer relative`}
                        title={badge.description}
                      >
                        <span className="text-2xl">{badge.icon}</span>
                        <span className={`${badge.color} font-black text-xs uppercase hidden sm:inline`}>
                          {badge.name}
                        </span>
                        {/* Tooltip on hover */}
                        <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-3 py-2 bg-black border-2 border-white text-white text-xs font-bold whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-50">
                          {badge.description}
                        </div>
                      </div>
                    );
                  })}
                  {badges.length > 5 && (
                    <div className="bg-gray-800 border-2 border-gray-600 px-3 py-2 flex items-center">
                      <span className="text-gray-400 font-black text-xs">+{badges.length - 5} MORE</span>
                    </div>
                  )}
                </div>
              </div>
            )}

            <button
              onClick={() => {
                setUsernameInput(username);
                setBioInput(bio);
                setShowEditProfile(true);
                setShowUsernameSetup(true);
              }}
              className="w-full bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-500 hover:to-indigo-500 text-white py-3 font-black text-sm uppercase border-3 border-blue-400 transition-all mb-2"
            >
              ✏️ EDIT PROFILE
            </button>

            <button
              onClick={() => shareToSocial('profile', {})}
              className="w-full bg-gradient-to-r from-lime-600 to-green-600 hover:from-lime-500 hover:to-green-500 text-black py-3 font-black text-sm uppercase border-3 border-lime-400 transition-all flex items-center justify-center gap-2"
            >
              <Share2 size={18} />
              SHARE MY PROGRESS
            </button>
          </div>

          {/* Stats Overview */}
          <div className="grid grid-cols-3 gap-3 sm:gap-4">
            <div className="bg-gradient-to-br from-lime-900 to-green-900 border-3 border-lime-500 p-4 text-center">
              <Flame className="text-lime-500 mx-auto mb-2" size={24} />
              <div className="text-3xl font-black text-lime-500">{getUserStats().streak}</div>
              <div className="text-xs font-black text-lime-400 uppercase mt-1">Current Streak</div>
            </div>
            <div className="bg-gradient-to-br from-orange-900 to-red-900 border-3 border-orange-500 p-4 text-center">
              <Zap className="text-orange-500 mx-auto mb-2" size={24} />
              <div className="text-3xl font-black text-orange-500">{getUserStats().totalSends}</div>
              <div className="text-xs font-black text-orange-400 uppercase mt-1">Total Sends</div>
            </div>
            <div className="bg-gradient-to-br from-purple-900 to-indigo-900 border-3 border-purple-500 p-4 text-center">
              <Trophy className="text-purple-500 mx-auto mb-2" size={24} />
              <div className="text-3xl font-black text-purple-500">{getUserStats().longestStreak}</div>
              <div className="text-xs font-black text-purple-400 uppercase mt-1">Best Streak</div>
            </div>
          </div>

          {/* Current Rank */}
          <div className={`bg-gradient-to-r ${getLevelInfo(sendItScore).currentLevel.bg} border-4 border-black p-6 text-center`}>
            <p className="text-white/60 text-sm font-black uppercase mb-2">Current Rank</p>
            <p className={`text-3xl sm:text-5xl font-black ${getLevelInfo(sendItScore).currentLevel.color} uppercase mb-2`}>
              {getLevelInfo(sendItScore).currentLevel.title}
            </p>
            <p className="text-white/80 text-sm font-bold">
              {getLevelInfo(sendItScore).currentLevel.message}
            </p>
          </div>

          {/* Recent Activity */}
          {getRecentSends().length > 0 && (
            <div className="bg-gray-900 border-3 border-purple-500 p-5 sm:p-6">
              <h3 className="text-xl font-black text-purple-400 uppercase mb-4">RECENT SENDS</h3>
              <div className="space-y-2">
                {getRecentSends().slice(0, 3).map((send, i) => (
                  <div key={i} className="p-3 bg-black border-2 border-gray-800">
                    <p className="text-white text-sm font-bold">{send.challenge}</p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* All Badges Section */}
          <BadgeDisplay badges={badges} BADGE_DEFINITIONS={BADGE_DEFINITIONS} />

          {/* Settings Section */}
          <div className="bg-gray-900 border-3 border-purple-500 p-5 sm:p-6">
            <h3 className="text-xl font-black text-purple-400 uppercase mb-4 border-b-2 border-purple-500 pb-2">SETTINGS</h3>
            <div className="space-y-3">
              <button
                onClick={() => {
                  const newState = !soundEnabled;
                  setSoundEnabled(newState);
                  storage.setSoundEnabled(newState);
                  if (newState) playSound('click');
                }}
                className={`w-full py-3 font-black text-sm uppercase border-3 transition-all ${soundEnabled
                  ? 'bg-lime-500 text-black border-lime-400'
                  : 'bg-gray-800 text-gray-400 border-gray-700'
                  }`}
              >
                {soundEnabled ? '🔊 SOUND ON' : '🔇 SOUND OFF'}
              </button>

              <button
                onClick={async () => {
                  if (notificationPermission === 'granted') {
                    alert('✓ Notifications are ON!\n\nYou\'ll get alerts for:\n• Streak warnings (12h before break)\n\nTo disable: Click the 🔒/🔓 icon in your browser\'s address bar → Site settings → Notifications → Block');
                  } else {
                    await requestNotificationPermission();
                  }
                }}
                className={`w-full py-3 font-black text-sm uppercase border-3 transition-all ${notificationPermission === 'granted'
                  ? 'bg-lime-500 text-black border-lime-400'
                  : notificationPermission === 'denied'
                    ? 'bg-red-800 text-red-400 border-red-700 hover:bg-red-700'
                    : 'bg-yellow-600 text-black border-yellow-400 hover:bg-yellow-500'
                  }`}
              >
                {notificationPermission === 'granted' ? '🔔 NOTIFICATIONS ON' :
                  notificationPermission === 'denied' ? '🔕 CLICK TO ENABLE' :
                    '🔔 ENABLE NOTIFICATIONS'}
              </button>
            </div>
          </div>

          {premiumAccountPrompt && (isAnonymousUser || !authEmailConfirmed) && (
            <p role="status" className="border-2 border-yellow-600 bg-black p-4 text-yellow-300 text-sm font-bold">
              To purchase SendIt Pass, first link and verify your email here. Use that same email at Gumroad so we can safely match your purchase to this account.
            </p>
          )}

          {!hasCloudSession ? (
            <section className="border-2 border-orange-700 bg-black p-4">
              <h3 className="text-orange-300 font-black text-sm uppercase tracking-wide">
                Account setup unavailable
              </h3>
              <p className="text-gray-400 text-xs font-bold mt-2">
                A connection to your account service is required to protect progress. Your progress is currently cached in this browser.
              </p>
            </section>
          ) : authEmail && !authEmailConfirmed ? (
            <section className="border-2 border-orange-600 bg-black p-4">
              <h3 className="text-orange-300 font-black text-sm uppercase tracking-wide">
                Confirm your email
              </h3>
              <p className="text-gray-400 text-xs font-bold mt-2">
                Check {authEmail} for the confirmation link. Confirm it before purchasing SendIt Pass, then use this same email at checkout.
              </p>
            </section>
          ) : isAnonymousUser ? (
            <section className="border-2 border-lime-600 bg-black p-4 space-y-3">
              <h3 className="text-lime-400 font-black text-sm uppercase tracking-wide">
                Protect your progress
              </h3>
              <p className="text-gray-400 text-xs font-bold">
                Add and confirm the email you plan to use at checkout. This protects your progress and lets us safely link a SendIt Pass purchase to this account.
              </p>
              <form onSubmit={handleLinkAccount} className="space-y-3">
                <input
                  type="email"
                  value={accountEmail}
                  onChange={(event) => setAccountEmail(event.target.value)}
                  autoComplete="email"
                  placeholder="Email address"
                  maxLength={254}
                  required
                  className="w-full bg-black border-2 border-lime-700 p-3 text-lime-300 font-bold text-sm placeholder-gray-600 focus:outline-none focus:border-lime-500"
                />
                <input
                  type="password"
                  value={accountPassword}
                  onChange={(event) => setAccountPassword(event.target.value)}
                  autoComplete="new-password"
                  placeholder="Create password (8+ characters)"
                  minLength={8}
                  required
                  className="w-full bg-black border-2 border-lime-700 p-3 text-lime-300 font-bold text-sm placeholder-gray-600 focus:outline-none focus:border-lime-500"
                />
                <input
                  type="password"
                  value={accountPasswordConfirm}
                  onChange={(event) => setAccountPasswordConfirm(event.target.value)}
                  autoComplete="new-password"
                  placeholder="Confirm password"
                  minLength={8}
                  required
                  className="w-full bg-black border-2 border-lime-700 p-3 text-lime-300 font-bold text-sm placeholder-gray-600 focus:outline-none focus:border-lime-500"
                />
                {accountLinkError && (
                  <p role="alert" className="text-red-400 text-sm font-bold">{accountLinkError}</p>
                )}
                {accountLinkMessage && (
                  <p role="status" className="text-lime-300 text-sm font-bold">{accountLinkMessage}</p>
                )}
                <button
                  type="submit"
                  disabled={accountLinking || !accountEmail || !accountPassword || !accountPasswordConfirm}
                  className="w-full py-3 bg-lime-500 hover:bg-lime-400 disabled:opacity-40 disabled:cursor-not-allowed text-black font-black text-sm uppercase tracking-wider border-2 border-lime-400 transition-all"
                >
                  {accountLinking ? 'LINKING LOGIN...' : 'CREATE LOGIN & PROTECT PROGRESS'}
                </button>
              </form>
            </section>
          ) : (
            <section className="border-2 border-lime-700 bg-black p-4">
              <h3 className="text-lime-400 font-black text-sm uppercase tracking-wide">
                Progress protected
              </h3>
              <p className="text-gray-400 text-xs font-bold mt-2">
                This account has a confirmed login{authEmail ? ` for ${authEmail}` : ''}. Use it to recover your progress.
              </p>
              <p className="text-gray-400 text-xs font-bold mt-2">
                Buy SendIt Pass with this same email to have the purchase linked to your account.
              </p>
            </section>
          )}

          {!isPremium && (
            <button
              onClick={openUpgradeModal}
              className="w-full py-3 bg-yellow-500 hover:bg-yellow-400 text-black font-black uppercase tracking-wider border-3 border-yellow-400 transition-all"
              style={{ boxShadow: '0 0 16px rgba(234,179,8,0.3)' }}
            >
              ⚡ GET SENDIT PASS
            </button>
          )}

          {/* Danger Zone */}
          {isDev && (
            <div className="bg-gradient-to-br from-red-900 to-black border-3 border-red-500 p-5 sm:p-6">
              <h3 className="text-xl font-black text-red-400 uppercase mb-4 border-b-2 border-red-500 pb-2">DEV TOOLS</h3>
              <div className="space-y-3">
                <button
                  type="button"
                  onClick={(e) => {
                    console.log('Advance day clicked');
                    testAdvanceDay();
                  }}
                  className="w-full bg-gradient-to-r from-blue-600 to-blue-800 hover:from-blue-700 hover:to-blue-900 text-white py-3 font-black text-sm uppercase border-3 border-blue-500 transition-all cursor-pointer"
                >
                  ⏰ ADVANCE TO NEXT DAY
                </button>
                <button
                  type="button"
                  onClick={(e) => {
                    console.log('Reset clicked');
                    resetAllData(e);
                  }}
                  className="w-full bg-gradient-to-r from-red-600 to-red-800 hover:from-red-700 hover:to-red-900 text-white py-3 font-black text-sm uppercase border-3 border-red-500 transition-all cursor-pointer"
                >
                  ⚠️ RESET ALL DATA
                </button>
              </div>
              <p className="text-center text-gray-500 text-xs mt-3 font-bold">
                Only visible on localhost
              </p>
            </div>
          )}
        </>
      )}
    </div>
  );
};
